(() => {
  const productCache = new Map();
  const fragmentCache = new Map();

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN'
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const escapeHtml = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const htmlToText = (html = '') => {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  };

  const rootPath = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const initPetPaw = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const animalTabs = [...root.querySelectorAll('[data-animal-tab]')];
    const subTabs = [...root.querySelectorAll('[data-subcategory-tab]')];
    const panels = [...root.querySelectorAll('[data-product-panel]')];
    const catalogTitle = root.querySelector('[data-catalog-title]');
    const searchInput = root.querySelector('[data-product-search]');
    const searchEmpty = root.querySelector('[data-search-empty]');

    const cartDrawer = root.querySelector('[data-cart-drawer]');
    const cartOverlay = root.querySelector('[data-cart-overlay]');
    const cartBody = root.querySelector('[data-cart-body]');
    const cartTotal = root.querySelector('[data-cart-total]');
    const cartCountEls = [...root.querySelectorAll('[data-cart-count]')];

    const quickOverlay = root.querySelector('[data-quick-overlay]');
    const quickModal = root.querySelector('[data-quick-view-modal]');
    const quickImage = root.querySelector('[data-quick-image]');
    const quickThumbs = root.querySelector('[data-quick-thumbs]');
    const quickVendor = root.querySelector('[data-quick-vendor]');
    const quickTitle = root.querySelector('[data-quick-title]');
    const quickStock = root.querySelector('[data-quick-stock]');
    const quickPrice = root.querySelector('[data-quick-price]');
    const quickCompare = root.querySelector('[data-quick-compare]');
    const quickDescription = root.querySelector('[data-quick-description]');
    const quickVariant = root.querySelector('[data-quick-variant]');
    const quickAdd = root.querySelector('[data-quick-add]');
    const quickFeedback = root.querySelector('[data-quick-feedback]');

    let activePanel = null;
    let quickProduct = null;
    let quickSelectedVariant = null;
    let activationSequence = 0;

    const setPageLocked = (locked) => {
      document.documentElement.style.overflow = locked ? 'hidden' : '';
    };

    const applyPanelSettings = (panel) => {
      const shipping = root.dataset.shippingNote || '';
      panel.querySelectorAll('[data-shipping-copy]').forEach((node) => {
        if (shipping) node.textContent = shipping;
      });
    };

    const updateSearch = () => {
      const query = (searchInput?.value || '').trim().toLowerCase();
      let visible = 0;

      if (!activePanel || activePanel.dataset.loading === 'true') {
        searchEmpty?.classList.add('pp-is-hidden');
        return;
      }

      activePanel.querySelectorAll('[data-product-card]').forEach((card) => {
        const haystack = (card.dataset.search || '').toLowerCase();
        const show = !query || haystack.includes(query);
        card.classList.toggle('pp-search-hidden', !show);
        if (show) visible += 1;
      });

      if (searchEmpty) {
        searchEmpty.classList.toggle('pp-is-hidden', visible > 0);
      }
    };

    const animatePanel = (panel) => {
      panel.classList.remove('pp-panel-animate');
      void panel.offsetWidth;
      panel.classList.add('pp-panel-animate');
    };

    const renderPanelError = (panel) => {
      const slot = panel.querySelector('[data-dynamic-slot]');
      if (!slot) return;

      slot.innerHTML = `
        <div class="pp-empty pp-load-error">
          <strong>No pudimos cargar esta categoría.</strong><br>
          <button type="button" class="pp-retry-load" data-retry-panel>Intentar de nuevo</button>
        </div>
      `;
    };

    const loadPanel = async (panel) => {
      if (!panel) return false;
      if (panel.dataset.loaded === 'true') return true;

      const url = panel.dataset.fragmentUrl;
      const slot = panel.querySelector('[data-dynamic-slot]');

      if (!url) {
        panel.dataset.loaded = 'true';
        return false;
      }

      panel.dataset.loading = 'true';
      searchEmpty?.classList.add('pp-is-hidden');

      try {
        let html = fragmentCache.get(url);

        if (!html) {
          const response = await fetch(url, {
            headers: { 'Accept': 'text/html' },
            credentials: 'same-origin'
          });

          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          html = await response.text();
          fragmentCache.set(url, html);
        }

        const parser = document.createElement('div');
        parser.innerHTML = html;
        const fetchedGrid = parser.querySelector('[data-fragment-grid]');

        if (!fetchedGrid) throw new Error('Fragment grid missing');

        const limit = Math.max(1, Number(panel.dataset.limit || root.dataset.productsLimit || 20));
        const cards = [...fetchedGrid.querySelectorAll('[data-product-card]')];
        cards.slice(limit).forEach((card) => card.remove());

        if (slot) {
          slot.innerHTML = '';
          slot.appendChild(fetchedGrid);
        }

        panel.dataset.loaded = 'true';
        applyPanelSettings(panel);
        return true;
      } catch (error) {
        panel.dataset.loaded = 'false';
        renderPanelError(panel);
        return false;
      } finally {
        panel.dataset.loading = 'false';
      }
    };

    const activateSubcategory = async (button, scroll = false) => {
      if (!button) return;

      const sequence = ++activationSequence;
      const panelId = button.dataset.panel;
      const panel = document.getElementById(panelId);
      if (!panel) return;

      subTabs.forEach((tab) => {
        tab.classList.toggle('pp-is-active', tab === button);
        tab.classList.remove('pp-is-loading');
      });
      button.classList.add('pp-is-loading');

      panels.forEach((item) => {
        item.classList.toggle('pp-is-hidden', item !== panel);
      });

      activePanel = panel;

      if (catalogTitle) {
        catalogTitle.textContent = button.textContent.trim();
      }

      const loaded = await loadPanel(panel);
      if (sequence !== activationSequence) return;

      button.classList.remove('pp-is-loading');

      if (loaded) {
        animatePanel(panel);
        updateSearch();
      }

      if (scroll) {
        const catalog = root.querySelector('.pp-section--catalog');
        if (catalog) {
          const top = catalog.getBoundingClientRect().top + window.scrollY - 118;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }
    };

    const activateAnimal = async (button, scroll = false) => {
      if (!button) return;

      const group = button.dataset.group;

      animalTabs.forEach((tab) => {
        const active = tab === button;
        tab.classList.toggle('pp-is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      const relevant = subTabs.filter((tab) => tab.dataset.group === group);

      subTabs.forEach((tab) => {
        tab.hidden = tab.dataset.group !== group;
        tab.classList.remove('pp-is-active', 'pp-is-loading');
      });

      panels.forEach((panel) => panel.classList.add('pp-is-hidden'));

      if (searchInput) searchInput.value = '';
      searchEmpty?.classList.add('pp-is-hidden');

      if (relevant[0]) {
        relevant[0].hidden = false;
        await activateSubcategory(relevant[0], scroll);
      } else {
        activePanel = null;
        if (catalogTitle) catalogTitle.textContent = 'Productos seleccionados';
      }
    };

    animalTabs.forEach((tab) => {
      tab.addEventListener('click', () => activateAnimal(tab, true));
    });

    subTabs.forEach((tab) => {
      tab.addEventListener('click', () => activateSubcategory(tab, false));
    });

    searchInput?.addEventListener('input', updateSearch);

    if (animalTabs[0]) {
      activateAnimal(animalTabs[0], false);
    }

    const updateCardAvailability = (card, available) => {
      const stock = card?.querySelector('[data-stock-status]');
      const add = card?.querySelector('[data-add-to-cart]');
      const label = card?.querySelector('[data-add-label]');

      if (stock) {
        stock.classList.toggle('pp-is-soldout', !available);
        stock.innerHTML = `<span></span>${available ? 'Disponible' : 'Agotado'}`;
      }

      if (add) add.disabled = !available;
      if (label) label.textContent = available ? 'Agregar al carrito' : 'Agotado';
    };

    const renderCart = (cart) => {
      cartCountEls.forEach((el) => {
        el.textContent = cart.item_count;
      });

      if (cartTotal) {
        cartTotal.textContent = formatMoney(cart.total_price);
      }

      if (!cartBody) return;

      if (!cart.items || !cart.items.length) {
        cartBody.innerHTML = '<div class="pp-cart-empty">Tu carrito está vacío.</div>';
        return;
      }

      cartBody.innerHTML = cart.items.map((item) => {
        const image = item.image
          ? `<div class="pp-cart-item__image"><img src="${escapeHtml(item.image)}" alt=""></div>`
          : '<div class="pp-cart-item__image"></div>';

        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? `<div class="pp-cart-item__meta">${escapeHtml(item.variant_title)}</div>`
          : '';

        return `
          <div class="pp-cart-item">
            ${image}
            <div>
              <p class="pp-cart-item__title">${escapeHtml(item.product_title)}</p>
              ${variant}
              <div class="pp-cart-item__meta">Cantidad: ${item.quantity}</div>
              <button class="pp-cart-remove" type="button" data-remove-key="${escapeHtml(item.key)}">
                Eliminar
              </button>
            </div>
            <div class="pp-cart-item__price">${formatMoney(item.final_line_price)}</div>
          </div>
        `;
      }).join('');
    };

    const refreshCart = async () => {
      if (cartBody) {
        cartBody.innerHTML = '<div class="pp-cart-loading">Cargando carrito…</div>';
      }

      try {
        const response = await fetch(`${rootPath()}cart.js`, {
          headers: { 'Accept': 'application/json' }
        });

        if (!response.ok) throw new Error('Cart load failed');

        const cart = await response.json();
        renderCart(cart);
      } catch (error) {
        if (cartBody) {
          cartBody.innerHTML = '<div class="pp-cart-empty">No pudimos cargar el carrito.</div>';
        }
      }
    };

    const openCart = async () => {
      if (!cartDrawer || !cartOverlay) return;

      await refreshCart();
      cartDrawer.classList.add('pp-is-open');
      cartDrawer.setAttribute('aria-hidden', 'false');
      cartOverlay.classList.remove('pp-is-hidden');
      setPageLocked(true);
    };

    const closeCart = () => {
      if (!cartDrawer || !cartOverlay) return;

      cartDrawer.classList.remove('pp-is-open');
      cartDrawer.setAttribute('aria-hidden', 'true');
      cartOverlay.classList.add('pp-is-hidden');
      setPageLocked(false);
    };

    root.querySelectorAll('[data-cart-open]').forEach((button) => {
      button.addEventListener('click', openCart);
    });

    root.querySelectorAll('[data-cart-close]').forEach((button) => {
      button.addEventListener('click', closeCart);
    });

    cartOverlay?.addEventListener('click', closeCart);

    const addVariantToCart = async (variantId, sourceButton = null, feedback = null) => {
      if (!variantId) return false;

      const original = sourceButton?.innerHTML;

      if (sourceButton) {
        sourceButton.disabled = true;
        sourceButton.textContent = 'Agregando…';
      }
      if (feedback) feedback.textContent = '';

      try {
        const response = await fetch(`${rootPath()}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }]
          })
        });

        if (!response.ok) throw new Error('Add failed');

        if (sourceButton) sourceButton.textContent = 'Agregado ✓';
        if (feedback) feedback.textContent = 'Producto agregado al carrito.';
        await refreshCart();

        setTimeout(() => {
          if (sourceButton) {
            sourceButton.innerHTML = original;
            sourceButton.disabled = false;
          }
        }, 1200);

        return true;
      } catch (error) {
        if (sourceButton) {
          sourceButton.innerHTML = original;
          sourceButton.disabled = false;
        }
        if (feedback) feedback.textContent = 'No se pudo agregar. Intenta otra vez.';
        return false;
      }
    };

    /* ---------------- QUICK VIEW ---------------- */
    const closeQuickView = () => {
      if (!quickModal || !quickOverlay) return;

      quickModal.classList.remove('pp-is-open');
      quickModal.setAttribute('aria-hidden', 'true');
      quickOverlay.classList.add('pp-is-hidden');
      quickProduct = null;
      quickSelectedVariant = null;
      setPageLocked(false);
    };

    const imageSrc = (image) => {
      if (!image) return '';
      if (typeof image === 'string') return image;
      return image.src || '';
    };

    const setQuickImage = (src) => {
      if (!quickImage || !src) return;
      quickImage.src = src;
    };

    const renderQuickThumbs = (product, activeSrc) => {
      if (!quickThumbs) return;

      const images = (product.images || []).slice(0, 6);
      quickThumbs.innerHTML = images.map((image, index) => {
        const src = imageSrc(image);
        return `
          <button
            type="button"
            class="pp-quick-thumb${src === activeSrc || (!activeSrc && index === 0) ? ' pp-is-active' : ''}"
            data-quick-thumb="${escapeHtml(src)}"
          >
            <img src="${escapeHtml(src)}" alt="">
          </button>
        `;
      }).join('');
    };

    const updateQuickVariant = (variant) => {
      if (!variant) return;

      quickSelectedVariant = variant;
      const available = Boolean(variant.available);

      if (quickPrice) quickPrice.textContent = formatMoney(variant.price);

      if (quickCompare) {
        const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
        quickCompare.textContent = hasCompare ? formatMoney(variant.compare_at_price) : '';
        quickCompare.classList.toggle('pp-is-hidden', !hasCompare);
      }

      if (quickStock) {
        quickStock.classList.toggle('pp-is-soldout', !available);
        quickStock.innerHTML = `<span></span><b>${available ? 'Disponible' : 'Agotado'}</b>`;
      }

      if (quickAdd) {
        quickAdd.disabled = !available;
        quickAdd.textContent = available ? 'Agregar al carrito' : 'Agotado';
      }

      const variantImage = imageSrc(variant.featured_image);
      if (variantImage) {
        setQuickImage(variantImage);

        quickThumbs?.querySelectorAll('.pp-quick-thumb').forEach((item) => {
          item.classList.toggle('pp-is-active', item.dataset.quickThumb === variantImage);
        });
      }
    };

    const renderQuickProduct = (product, preferredVariantId = null) => {
      quickProduct = product;

      if (quickVendor) quickVendor.textContent = product.vendor || '';
      if (quickTitle) quickTitle.textContent = product.title || '';

      if (quickDescription) {
        const text = htmlToText(product.description || '');
        quickDescription.textContent = text.length > 430 ? `${text.slice(0, 430)}…` : text;
      }

      const firstImage = imageSrc(product.featured_image) || imageSrc((product.images || [])[0]);
      setQuickImage(firstImage);
      if (quickImage) quickImage.alt = product.title || '';
      renderQuickThumbs(product, firstImage);

      if (quickVariant) {
        quickVariant.innerHTML = (product.variants || []).map((variant) => `
          <option value="${variant.id}" ${variant.available ? '' : 'disabled'}>
            ${escapeHtml(variant.title === 'Default Title' ? 'Única opción' : variant.title)}${variant.available ? '' : ' — Agotado'}
          </option>
        `).join('');
      }

      const variants = product.variants || [];
      const selected =
        variants.find((variant) => String(variant.id) === String(preferredVariantId) && variant.available) ||
        variants.find((variant) => variant.available) ||
        variants[0];

      if (selected && quickVariant) quickVariant.value = String(selected.id);
      updateQuickVariant(selected);
    };

    const fetchProduct = async (handle) => {
      if (productCache.has(handle)) return productCache.get(handle);

      const response = await fetch(`${rootPath()}products/${encodeURIComponent(handle)}.js`, {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) throw new Error('Product load failed');

      const product = await response.json();
      productCache.set(handle, product);
      return product;
    };

    const openQuickView = async (handle, card = null) => {
      if (!handle || !quickModal || !quickOverlay) return;

      const preferredVariantId = card?.querySelector('[data-variant-select]')?.value || null;

      quickModal.classList.add('pp-is-open');
      quickModal.setAttribute('aria-hidden', 'false');
      quickOverlay.classList.remove('pp-is-hidden');
      setPageLocked(true);

      if (quickTitle) quickTitle.textContent = 'Cargando producto…';
      if (quickVendor) quickVendor.textContent = '';
      if (quickDescription) quickDescription.textContent = '';
      if (quickThumbs) quickThumbs.innerHTML = '';
      if (quickImage) quickImage.removeAttribute('src');
      if (quickAdd) quickAdd.disabled = true;
      if (quickFeedback) quickFeedback.textContent = '';

      try {
        const product = await fetchProduct(handle);
        renderQuickProduct(product, preferredVariantId);
      } catch (error) {
        if (quickTitle) quickTitle.textContent = 'No pudimos cargar este producto.';
        if (quickDescription) quickDescription.textContent = 'Intenta nuevamente.';
      }
    };

    root.addEventListener('click', async (event) => {
      const retry = event.target.closest('[data-retry-panel]');
      if (retry && root.contains(retry)) {
        const panel = retry.closest('[data-product-panel]');
        if (panel) {
          panel.dataset.loaded = 'false';
          const slot = panel.querySelector('[data-dynamic-slot]');
          if (slot) {
            slot.innerHTML = '<div class="pp-catalog-loader"><div class="pp-loader-card"></div><div class="pp-loader-card"></div><div class="pp-loader-card"></div><div class="pp-loader-card"></div><div class="pp-loader-card"></div></div>';
          }
          const tab = subTabs.find((item) => item.dataset.panel === panel.id);
          await activateSubcategory(tab, false);
        }
        return;
      }

      const chip = event.target.closest('[data-variant-chip]');
      if (chip && root.contains(chip)) {
        const card = chip.closest('[data-product-card]');
        const hidden = card?.querySelector('input[data-variant-select]');
        const price = card?.querySelector('[data-price]');
        const compare = card?.querySelector('[data-compare-price]');
        const available = chip.dataset.available === 'true';

        card?.querySelectorAll('[data-variant-chip]').forEach((item) => {
          item.classList.toggle('pp-is-selected', item === chip);
        });

        if (hidden) {
          hidden.value = chip.dataset.variantId || '';
          hidden.dataset.available = String(available);
        }
        if (price) price.textContent = chip.dataset.price || '';
        if (compare) {
          const value = chip.dataset.comparePrice || '';
          compare.textContent = value;
          compare.classList.toggle('pp-is-hidden', !value);
        }
        updateCardAvailability(card, available);
        return;
      }

      const quick = event.target.closest('[data-quick-view]');
      if (quick && root.contains(quick)) {
        event.preventDefault();
        const card = quick.closest('[data-product-card]');
        await openQuickView(quick.dataset.handle || card?.dataset.productHandle, card);
        return;
      }

      const add = event.target.closest('[data-add-to-cart]');
      if (add && root.contains(add)) {
        const card = add.closest('[data-product-card]');
        const variantField = card?.querySelector('[data-variant-select]');
        const feedback = card?.querySelector('[data-card-feedback]');
        const variantId = variantField?.value;
        const available = variantField?.dataset.available !== 'false';

        if (!variantId || !available) return;

        const added = await addVariantToCart(variantId, add, feedback);
        if (added) await openCart();
      }
    });

    root.addEventListener('change', (event) => {
      const select = event.target.closest('select[data-variant-select]');
      if (!select || !root.contains(select)) return;

      const card = select.closest('[data-product-card]');
      const option = select.selectedOptions[0];
      const price = card?.querySelector('[data-price]');
      const compare = card?.querySelector('[data-compare-price]');
      const available = option.dataset.available === 'true';

      select.dataset.available = String(available);
      if (price) price.textContent = option.dataset.price || '';
      if (compare) {
        const value = option.dataset.comparePrice || '';
        compare.textContent = value;
        compare.classList.toggle('pp-is-hidden', !value);
      }
      updateCardAvailability(card, available);
    });

    root.querySelectorAll('[data-quick-close]').forEach((button) => {
      button.addEventListener('click', closeQuickView);
    });

    quickOverlay?.addEventListener('click', closeQuickView);

    quickThumbs?.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-quick-thumb]');
      if (!thumb) return;

      const src = thumb.dataset.quickThumb || '';
      setQuickImage(src);
      quickThumbs.querySelectorAll('.pp-quick-thumb').forEach((item) => {
        item.classList.toggle('pp-is-active', item === thumb);
      });
    });

    quickVariant?.addEventListener('change', () => {
      if (!quickProduct) return;
      const variant = (quickProduct.variants || []).find(
        (item) => String(item.id) === String(quickVariant.value)
      );
      updateQuickVariant(variant);
    });

    quickAdd?.addEventListener('click', async () => {
      if (!quickSelectedVariant || !quickSelectedVariant.available) return;

      const added = await addVariantToCart(
        quickSelectedVariant.id,
        quickAdd,
        quickFeedback
      );

      if (added) {
        closeQuickView();
        await openCart();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (quickModal?.classList.contains('pp-is-open')) closeQuickView();
      else if (cartDrawer?.classList.contains('pp-is-open')) closeCart();
    });

    refreshCart();
  };

  const boot = () => {
    document.querySelectorAll('[data-petpaw-ads-root]').forEach(initPetPaw);
  };

  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();
